import {
  useEffect,
  useId,
  useState,
  type ButtonHTMLAttributes,
  type RefObject,
  type InputHTMLAttributes,
  type ReactNode,
  type SelectHTMLAttributes,
  type TextareaHTMLAttributes,
} from 'react';
import type { Monto } from '../api/tipos';
import { formatearDocumento, normalizarDocumento } from '../comun/documento';
import { leerMonto, monto as formatearMonto } from '../comun/formato';
import { Icono } from './Icono';

/**
 * Los controles. Un componente por trabajo y las variantes como PROPS, no como componentes
 * nuevos: `<Boton variante="peligro">`, no `<BotonPeligro>`. Con lo segundo, el día que se
 * cambia la altura de los botones hay que cambiarla en once lugares.
 */

// --- Botón ---------------------------------------------------------------------

interface PropsBoton extends ButtonHTMLAttributes<HTMLButtonElement> {
  variante?: 'primario' | 'secundario' | 'fantasma' | 'peligro';
  tamano?: 'normal' | 'chico' | 'grande' | 'mostrador';
  icono?: string;
  cargando?: boolean;
}

export function Boton({ variante = 'secundario', tamano = 'normal', icono, cargando, children, className, disabled, ...resto }: PropsBoton) {
  return (
    <button
      type="button"
      className={`boton boton--${variante} boton--${tamano} ${className ?? ''}`}
      disabled={disabled || cargando}
      aria-busy={cargando || undefined}
      {...resto}
    >
      {cargando ? <span className="giro" aria-hidden /> : icono ? <Icono nombre={icono} /> : null}
      {children ? <span>{children}</span> : null}
    </button>
  );
}

// --- Campo: etiqueta, ayuda y error alrededor de cualquier control ------------------

export function Campo({
  etiqueta,
  ayuda,
  error,
  children,
  ancho,
  id,
  obligatorio,
}: {
  etiqueta: string;
  ayuda?: ReactNode;
  error?: string | null;
  children: (id: string) => ReactNode;
  ancho?: 'completo' | 'medio' | 'tercio' | 'cuarto';
  id?: string;
  obligatorio?: boolean;
}) {
  const generado = useId();
  const idControl = id ?? generado;
  return (
    <div className={`campo ${ancho ? `campo--${ancho}` : ''} ${error ? 'campo--error' : ''}`}>
      <label htmlFor={idControl} className="campo__etiqueta">
        {etiqueta}
        {obligatorio ? <span className="campo__obligatorio" aria-hidden> *</span> : null}
      </label>
      {children(idControl)}
      {error ? (
        <p className="campo__error" role="alert">
          <Icono nombre="alerta" />
          {error}
        </p>
      ) : ayuda ? (
        <p className="campo__ayuda">{ayuda}</p>
      ) : null}
    </div>
  );
}

export function Entrada(props: InputHTMLAttributes<HTMLInputElement>) {
  return <input {...props} className={`control ${props.className ?? ''}`} />;
}

export function Selector(props: SelectHTMLAttributes<HTMLSelectElement>) {
  return (
    <div className="control-select">
      <select {...props} className={`control ${props.className ?? ''}`} />
      <Icono nombre="abajo" />
    </div>
  );
}

export function Area(props: TextareaHTMLAttributes<HTMLTextAreaElement>) {
  return <textarea rows={3} {...props} className={`control control--area ${props.className ?? ''}`} />;
}

// --- Monto -----------------------------------------------------------------------

/**
 * Monto con separador de miles mientras se escribe. El valor es SIEMPRE texto: es un BigInt en
 * guaraníes y nunca pasa por `Number`.
 */
export function CampoMonto({
  valor,
  alCambiar,
  moneda = 'PYG',
  id,
  autoFocus,
  tamano = 'normal',
  disabled,
  placeholder,
}: {
  valor: Monto | null;
  alCambiar: (valor: Monto | null) => void;
  moneda?: string;
  id?: string;
  autoFocus?: boolean;
  tamano?: 'normal' | 'grande';
  disabled?: boolean;
  placeholder?: string;
}) {
  const [texto, setTexto] = useState(valor === null ? '' : formatearMonto(valor, moneda, { simbolo: false }));

  useEffect(() => {
    const actual = leerMonto(texto);
    if (actual !== valor) setTexto(valor === null ? '' : formatearMonto(valor, moneda, { simbolo: false }));
    // Solo cuando el valor cambia desde afuera.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [valor, moneda]);

  return (
    <div className={`control-monto ${tamano === 'grande' ? 'control-monto--grande' : ''}`}>
      <span className="control-monto__simbolo">{moneda === 'PYG' ? 'Gs.' : moneda}</span>
      <input
        id={id}
        inputMode="numeric"
        autoComplete="off"
        className="control control--monto"
        value={texto}
        autoFocus={autoFocus}
        disabled={disabled}
        placeholder={placeholder ?? '0'}
        onChange={(evento) => {
          const leido = leerMonto(evento.target.value);
          setTexto(leido === null ? '' : formatearMonto(leido, moneda, { simbolo: false }));
          alCambiar(leido);
        }}
      />
    </div>
  );
}

// --- Cédula ----------------------------------------------------------------------

/**
 * EL CAMPO DE CÉDULA. En `tamano="mostrador"` es el control principal de la pantalla
 * principal: se tipea sin mirar el teclado, se lee de lejos y se usa sin mouse.
 *
 * Normaliza mientras se escribe (puntos, guiones, espacios) y muestra los puntos de miles,
 * que es como está impreso en el carnet que la persona tiene en la mano. Lo que sale para la
 * API son solo dígitos.
 */
export function CampoCedula({
  valor,
  alCambiar,
  alEnviar,
  id,
  tamano = 'normal',
  referencia,
  autoFocus,
  disabled,
  placeholder = 'Cédula',
}: {
  valor: string;
  alCambiar: (documento: string) => void;
  alEnviar?: () => void;
  id?: string;
  tamano?: 'normal' | 'mostrador';
  referencia?: RefObject<HTMLInputElement | null>;
  autoFocus?: boolean;
  disabled?: boolean;
  placeholder?: string;
}) {
  return (
    <input
      id={id}
      ref={referencia}
      className={`control control--cedula ${tamano === 'mostrador' ? 'control--cedula-mostrador' : ''}`}
      value={formatearDocumento(valor)}
      onChange={(evento) => alCambiar(normalizarDocumento(evento.target.value))}
      onKeyDown={(evento) => {
        if (evento.key === 'Enter') {
          evento.preventDefault();
          alEnviar?.();
        }
      }}
      inputMode="numeric"
      autoComplete="off"
      autoCorrect="off"
      spellCheck={false}
      enterKeyHint="search"
      autoFocus={autoFocus}
      disabled={disabled}
      placeholder={placeholder}
      aria-label="Número de cédula"
    />
  );
}

// --- Interruptor, segmentado, buscador ---------------------------------------------

export function Interruptor({
  activo,
  alCambiar,
  etiqueta,
  ayuda,
  disabled,
}: {
  activo: boolean;
  alCambiar: (valor: boolean) => void;
  etiqueta: string;
  ayuda?: string;
  disabled?: boolean;
}) {
  return (
    <label className={`interruptor ${disabled ? 'interruptor--deshabilitado' : ''}`}>
      <input type="checkbox" checked={activo} disabled={disabled} onChange={(e) => alCambiar(e.target.checked)} />
      <span className="interruptor__pista" aria-hidden>
        <span className="interruptor__perilla" />
      </span>
      <span className="interruptor__texto">
        <span>{etiqueta}</span>
        {ayuda ? <small>{ayuda}</small> : null}
      </span>
    </label>
  );
}

/** Opciones cortas lado a lado: ahorran un clic frente a un desplegable. */
export function Segmentado<T extends string>({
  opciones,
  valor,
  alCambiar,
  etiqueta,
}: {
  opciones: Array<{ valor: T; texto: string; icono?: string; cuenta?: number }>;
  valor: T;
  alCambiar: (valor: T) => void;
  etiqueta: string;
}) {
  return (
    <div className="segmentado" role="radiogroup" aria-label={etiqueta}>
      {opciones.map((opcion) => (
        <button
          key={opcion.valor}
          type="button"
          role="radio"
          aria-checked={valor === opcion.valor}
          className={`segmentado__opcion ${valor === opcion.valor ? 'segmentado__opcion--activa' : ''}`}
          onClick={() => alCambiar(opcion.valor)}
        >
          {opcion.icono ? <Icono nombre={opcion.icono} /> : null}
          {opcion.texto}
          {opcion.cuenta !== undefined ? <span className="segmentado__cuenta">{opcion.cuenta}</span> : null}
        </button>
      ))}
    </div>
  );
}

export function Buscador({
  valor,
  alCambiar,
  placeholder,
  autoFocus,
}: {
  valor: string;
  alCambiar: (valor: string) => void;
  placeholder: string;
  autoFocus?: boolean;
}) {
  return (
    <div className="buscador">
      <Icono nombre="buscar" />
      <input
        type="search"
        className="control buscador__entrada"
        value={valor}
        placeholder={placeholder}
        autoFocus={autoFocus}
        onChange={(e) => alCambiar(e.target.value)}
        aria-label={placeholder}
      />
      {valor ? (
        <button type="button" className="buscador__limpiar" onClick={() => alCambiar('')} aria-label="Limpiar búsqueda">
          <Icono nombre="cerrar" />
        </button>
      ) : null}
    </div>
  );
}

/**
 * Retrasa un valor para no pedirle a la API una consulta por tecla. En el mostrador el
 * retraso es corto: la consulta tiene que estar lista antes de que el dedo llegue al Enter.
 */
export function useDemorado<T>(valor: T, ms = 250): T {
  const [demorado, setDemorado] = useState(valor);
  useEffect(() => {
    const temporizador = setTimeout(() => setDemorado(valor), ms);
    return () => clearTimeout(temporizador);
  }, [valor, ms]);
  return demorado;
}
